// The rest of this file calls the global `axios` (the app instance from
// plugins/axios.js). The package is imported under another name so those calls
// keep their baseURL, Authorization header and refresh interceptor.
import axiosPkg from 'axios';
var getData = (thenable) => thenable.then(R.prop('data'));
import {
    getAdrielJwtExpireTimeMilliseconds,
    getAdrielJwtRefreshTokenExpireTimeMilliseconds,
    isTokenExpiredByMilliseconds,
} from '@/utils/utils';
import { Subject, from } from 'rxjs';
import { pluck } from 'rxjs/operators';

const baseURL = '/auth';

async function register(params) {
    const res = await axios.post(`${baseURL}/register`, params);
    return res.data;
}

async function login({ email, password, invite_token }) {
    const res = await axios.post(`${baseURL}/login`, {
        email,
        password,
        invite_token,
    });
    return res.data;
}

var logout = () =>
    from(axios.get(`${baseURL}/logout`))
        .pipe(pluck('data'))
        .toPromise();

var session = () =>
    from(axios.get(`${baseURL}/user`, { cache: false }))
        .pipe(pluck('data'))
        .toPromise();

var forgot = R.compose(getData, (email) =>
    axios.post(`${baseURL}/forgot`, { email })
);

var reset = R.compose(getData, (token, password) =>
    axios.post(`${baseURL}/reset`, { token, password })
);

async function updatePassword(password, newPassword) {
    const res = await axios.put(`${baseURL}/password`, {
        password,
        newPassword,
    });
    return res.data;
}

async function verifyEmail(token) {
    const res = await axios.get(`${baseURL}/verify`, {
        params: {
            token,
        },
    });
    return res.data;
}

async function resendEmail({ email }) {
    const res = await axios.post(`${baseURL}/resend`, {
        email,
    });
    return res.data;
}

// notepad image with sharable url
async function getJwtTokenWithHeader({ key = 'viewKey', value }) {
    const headers = value && {
        [key]: value,
    };

    const res = await axios.get(`${baseURL}/mediaToken`, {
        headers,
    });
    return res.data;
}

function postOauthRegister(data) {
    return axios.post(`${baseURL}/oauthRegister`, data);
}

function validateSocialLogin({ provider, query }) {
    return axios
        .get(`${baseURL}/${provider}/login`, { params: query })
        .then(R.prop('data'));
}

function refreshAccessToken({ refreshToken }) {
    return axios
        .post(`${baseURL}/refreshJWT`, {
            refresh_token: refreshToken,
        })
        .then(R.prop('data'));
}

/**
 * `POST /auth/mfa/verify` runs before the user has a session: the only
 * credential is the one-time `mfa_token`. It must not carry a Bearer token left
 * over from an earlier session, and a 401 from it (wrong code, expired
 * challenge) must not go through the app instance's refresh interceptor.
 */
// Same baseURL as plugins/axios.js. It is repeated rather than read from the app
// instance because plugins/axios.js imports this module, so the instance does
// not exist yet when this file is evaluated.
const mfaAxios = axiosPkg.create({
    baseURL: process.env.VUE_APP_API_URL || '/api',
    withCredentials: true,
});

mfaAxios.interceptors.request.use((config) => {
    // Defensive: a fresh instance does not inherit the app instance's default
    // header, but never let a stale Bearer reach the challenge endpoint.
    if (config.headers) delete config.headers.Authorization;
    return config;
});

async function verifyMfa({ mfa_token, method, code }) {
    const res = await mfaAxios.post(`${baseURL}/mfa/verify`, {
        mfa_token,
        method,
        code,
    });
    return res.data;
}

async function getMfaMethods() {
    const res = await axios.get(`${baseURL}/mfa/methods`);
    return res.data;
}

async function getMfaSetupNudge() {
    const res = await axios.get('/user/mfaSetupNudge');
    return res.data;
}

async function dismissMfaSetupNudge() {
    const res = await axios.post('/user/mfaSetupNudge/dismiss');
    return res.data;
}

const setAccessToken$ = new Subject();

function isJwtExpired() {
    const expMs = Number(getAdrielJwtExpireTimeMilliseconds());
    return expMs ? isTokenExpiredByMilliseconds(expMs) : true;
}

function isRefreshTokenExpired() {
    const expMs = Number(getAdrielJwtRefreshTokenExpireTimeMilliseconds());
    return expMs ? isTokenExpiredByMilliseconds(expMs) : true;
}

export default {
    register,
    login,
    logout,
    session,
    forgot,
    reset,
    updatePassword,
    verifyEmail,
    resendEmail,
    getJwtTokenWithHeader,
    postOauthRegister,
    validateSocialLogin,
    refreshAccessToken,
    setAccessToken$,
    isJwtExpired,
    isRefreshTokenExpired,
    verifyMfa,
    getMfaMethods,
    getMfaSetupNudge,
    dismissMfaSetupNudge,
};
